import React, { useEffect, useState } from "react";
import util from "../../utils/miniUtils";

import MaterialTable from "@material-table/core";
import { ThemeProvider } from "@mui/material/styles";
import UserProfile from "./UserProfile";
import { ADD_USER, ADMIN_EMAIL, EDIT_USER, ADMIN_USER, EMPLOYEE_USER, MECHANIC_USER, PROVIDER_USER } from "../../utils/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle, faUserSlash, faUserCheck, faTrashAlt, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useWindowResize } from "../../utils/useWindowResize";
import { formatPhone } from "./phone";
import "./Users.css";

const ROLE_BADGES = {
    [ADMIN_USER]: "bg-primary",
    [EMPLOYEE_USER]: "bg-info text-dark",
    [MECHANIC_USER]: "bg-secondary",
    [PROVIDER_USER]: "bg-dark",
};

const STATUS_INACTIVE = 0;

const isPendingActivation = ( user ) => user.status !== STATUS_INACTIVE && !user.activated_at;

export default function Users() {

    const [users, setUsers] = useState( [] );
    const [showProfile, setShowProfile] = useState( false );
    const [user_id, setUserId] = useState( null );
    const [userAction, setUserAction] = useState( ADD_USER );
    const [loading, setLoading] = useState( false );
    const startLoading = () => setLoading( true );
    const stopLoading = () => setLoading( false );
    const { height } = useWindowResize();
    const [showAlert, setShowAlert] = useState( {
        delete: false,
        reactivate: false,
        forceDelete: false,
        duplicate: false
    } );
    const MySwal = withReactContent( Swal );
    useEffect( () => {
        getUsersList().then( null );
    }, [] );



    async function getUsersList() {
        try {
            startLoading();
            const request = await util.axios.get( `users/list` );
            let { error, msg, data } = request.data;
            if ( error ) throw msg;
            stopLoading();
            setUsers( data );
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    }

    async function deleteUser() {
        try {
            startLoading();
            const request = await util.axios.delete( `users/${user_id}` );
            let { error, msg } = request.data;
            if ( error ) throw msg;
            stopLoading();
            util.toast().success( msg );
            await getUsersList();
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    }

    async function forceDeleteUser() {
        try {
            startLoading();
            const request = await util.axios.delete( `users/force-delete/${user_id}` );
            let { error, msg } = request.data;
            if ( error ) throw msg;
            stopLoading();
            util.toast().success( msg );
            await getUsersList();
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    }

    async function reactivateUser() {
        try {
            startLoading();
            const request = await util.axios.get( `users/reactivate-user/${user_id}` );
            let { error, msg } = request.data;
            if ( error ) throw msg;
            stopLoading();
            util.toast().success( msg );
            await getUsersList();
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    }

    async function resendActivation( id ) {
        try {
            startLoading();
            const request = await util.axios.post( `users/${id}/resend-activation` );
            let { error, msg } = request.data;
            if ( error ) throw msg;
            stopLoading();
            util.toast().success( msg );
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    }

    // SweetAlert2 for delete user (Deactivate)
    useEffect( () => {
        if ( showAlert.delete ) {
            MySwal.fire( {
                icon: 'warning',
                title: '⚠️ Desactivar Usuario',
                html: '<p>El usuario <strong>no podrá acceder</strong> al sistema.</p><p>Podrás reactivarlo más adelante.</p>',
                showCancelButton: true,
                confirmButtonText: 'Sí, desactivar usuario',
                cancelButtonText: 'Cancelar',
                customClass: {
                    confirmButton: 'btn btn-warning',
                    cancelButton: 'btn btn-secondary'
                },
                buttonsStyling: false
            } ).then( ( result ) => {
                setShowAlert( { ...showAlert, delete: false } );
                if ( result.isConfirmed ) {
                    deleteUser();
                }
            } );
        }
    }, [ showAlert.delete ] );

    // SweetAlert2 for reactivate user
    useEffect( () => {
        if ( showAlert.reactivate ) {
            MySwal.fire( {
                icon: 'info',
                title: '✅ Reactivar Usuario',
                html: '<p>El usuario <strong>podrá acceder</strong> nuevamente al sistema.</p><p>Recuperará todos sus permisos y accesos.</p>',
                showCancelButton: true,
                confirmButtonText: 'Sí, reactivar usuario',
                cancelButtonText: 'Cancelar',
                customClass: {
                    confirmButton: 'btn btn-info',
                    cancelButton: 'btn btn-secondary'
                },
                buttonsStyling: false
            } ).then( ( result ) => {
                setShowAlert( { ...showAlert, reactivate: false } );
                if ( result.isConfirmed ) {
                    reactivateUser();
                }
            } );
        }
    }, [ showAlert.reactivate ] );

    // SweetAlert2 for force delete user
    useEffect( () => {
        if ( showAlert.forceDelete ) {
            MySwal.fire( {
                icon: 'error',
                title: '🗑️ Eliminar Usuario Permanentemente',
                html: '<p><strong>Esta acción NO se puede deshacer.</strong></p><p>El usuario será <strong>eliminado completamente</strong> de la base de datos.</p><p>Todos sus datos y registros asociados serán removidos.</p>',
                showCancelButton: true,
                confirmButtonText: 'Sí, eliminar permanentemente',
                cancelButtonText: 'Cancelar',
                customClass: {
                    confirmButton: 'btn btn-danger',
                    cancelButton: 'btn btn-secondary'
                },
                buttonsStyling: false
            } ).then( ( result ) => {
                setShowAlert( { ...showAlert, forceDelete: false } );
                if ( result.isConfirmed ) {
                    forceDeleteUser();
                }
            } );
        }
    }, [ showAlert.forceDelete ] );

    return (
        <div className="container-fluid mb-3 mt-3">

            <div className="row">
                <div className="col-md-12">
                    <ThemeProvider theme={util.defaultTableThemeSlim}>
                        <MaterialTable
                            title="Basic Search Preview"
                            columns={[
                                {
                                    title: 'Nombre', field: 'name',
                                    render: data => {
                                        if ( data.email === ADMIN_EMAIL ) return <b>{data.name}</b>;
                                        return (
                                            <span>
                                                <span
                                                    onClick={() => {
                                                        setUserAction( EDIT_USER );
                                                        setUserId( data.id );
                                                        setShowProfile( true );
                                                    }}
                                                    className="likeLink">
                                                    {data.name}
                                                </span>
                                                {String( data.id ) === String( util.user.id ) && (
                                                    <span className="badge rounded-pill bg-light text-dark border users-self-badge">Tú</span>
                                                )}
                                            </span>
                                        );
                                    }
                                },
                                {
                                    title: 'Correo', field: 'email',
                                    render: data => {
                                        if ( data.email === ADMIN_EMAIL ) {
                                            return <b>{data.email}</b>;
                                        } else {
                                            return data.email;
                                        }
                                    }
                                },
                                {
                                    title: 'Teléfono', field: 'phone',
                                    render: data => {
                                        if ( data.email === ADMIN_EMAIL || !data.phone ) return <span className="text-muted">—</span>;
                                        return <a className="users-phone" href={`tel:${data.phone}`}>{formatPhone( data.phone )}</a>;
                                    }
                                },
                                {
                                    title: 'Rol', field: 'role',
                                    render: data => {
                                        if ( data.email === ADMIN_EMAIL ) {
                                            return <span className="badge rounded-pill bg-dark users-badge">Root</span>;
                                        }
                                        return (
                                            <span className={`badge rounded-pill users-badge ${ROLE_BADGES[data.role] || "bg-light text-dark"}`}>
                                                {util.userRole( data.role )}
                                            </span>
                                        );

                                    }
                                },
                                {
                                    title: 'Estado', field: 'status',
                                    render: data => {
                                        if ( data.email === ADMIN_EMAIL ) {
                                            return <span className="badge rounded-pill bg-dark users-badge">Permanente</span>;
                                        }
                                        if ( data.status === STATUS_INACTIVE ) {
                                            return <span className="badge rounded-pill bg-secondary users-badge">{util.userStatus( data.status )}</span>;
                                        }
                                        // Aún no define su contraseña con el link del correo de activación
                                        if ( isPendingActivation( data ) ) {
                                            return <span className="badge rounded-pill bg-warning text-dark users-badge">Pendiente de activación</span>;
                                        }
                                        return <span className="badge rounded-pill bg-success users-badge">{util.userStatus( data.status )}</span>;
                                    }
                                },
                                {
                                    title: 'Acciones', sorting: false, render: data => {
                                        // Ni el root ni el usuario con sesión iniciada pueden desactivarse o eliminarse
                                        if ( data.email === ADMIN_EMAIL || String( data.id ) === String( util.user.id ) ) return null;
                                        const inactive = data.status === STATUS_INACTIVE;
                                        return (
                                            <div className="users-actions">
                                                {isPendingActivation( data ) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => resendActivation( data.id )}
                                                        className="btn btn-sm btn-outline-primary users-action-btn"
                                                        title="Reenviar el correo con el link para definir su contraseña"
                                                    >
                                                        <FontAwesomeIcon icon={faPaperPlane} />
                                                        <span>Reenviar correo</span>
                                                    </button>
                                                )}
                                                {inactive ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setUserId( data.id );
                                                            setShowAlert( { ...showAlert, reactivate: true } );
                                                        }}
                                                        className="btn btn-sm btn-outline-success users-action-btn"
                                                        title="Reactivar usuario: podrá acceder de nuevo al sistema"
                                                    >
                                                        <FontAwesomeIcon icon={faUserCheck} />
                                                        <span>Reactivar</span>
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setUserId( data.id );
                                                            setShowAlert( { ...showAlert, delete: true } );
                                                        }}
                                                        className="btn btn-sm btn-outline-warning users-action-btn"
                                                        title="Desactivar usuario: no podrá acceder al sistema, pero podrá ser reactivado"
                                                    >
                                                        <FontAwesomeIcon icon={faUserSlash} />
                                                        <span>Desactivar</span>
                                                    </button>
                                                )}
                                                {/* Solo se puede eliminar si no tiene movimientos de inventario ni historial */}
                                                {data.canDelete && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setUserId( data.id );
                                                            setShowAlert( { ...showAlert, forceDelete: true } );
                                                        }}
                                                        className="btn btn-sm btn-outline-danger users-action-btn"
                                                        title="Eliminar usuario: se borra permanentemente de la base de datos"
                                                    >
                                                        <FontAwesomeIcon icon={faTrashAlt} />
                                                        <span>Eliminar</span>
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    }
                                },
                            ]}
                            data={users}
                            options={{
                                toolbarButtonAlignment: "left",
                                showTitle: false,
                                paginationPosition: "both",
                                columnsButton: true,
                                pageSize: 10,
                                searchFieldAlignment: "left",
                                emptyRowsWhenPaging: true,
                                padding: "dense",
                                showTextRowsSelected: false,
                                headerStyle: { position: 'sticky', top: 0 }, 
                                maxBodyHeight: height - 300
                            }}
                            actions={[
                                {
                                    icon: () => (
                                        <div className={'btn btn-primary btn-sm'}>
                                            <FontAwesomeIcon
                                                icon={faPlusCircle}
                                                className="mr-3"
                                            />
                                            <span className="p-2">Crear Usuario</span>
                                        </div>
                                    ),
                                    tooltip: 'Crear nuevo usuario',
                                    isFreeAction: true,
                                    onClick: () => {
                                        setUserAction( ADD_USER );
                                        setShowProfile( true );
                                    }
                                },
                            ]}
                        />
                    </ThemeProvider>
                </div>
            </div>

            {
                showProfile ?
                    <UserProfile
                        user_id={user_id}
                        show={showProfile}
                        handleClose={() => setShowProfile( false )}
                        reloadTable={getUsersList}
                        action={userAction}
                    />
                    :
                    null
            }


            {/* SweetAlert2 dialogs handled by useEffect hooks */}
            {util.LOADING_SCREEN( loading )}
        </div>
    );
}